import React from 'react'

import { useDecision } from '@optimizely/react-sdk'
import { SocialIconShareButtons } from 'SocialLinks/SocialShareButtons'
import { SocialShareSheetCTA } from 'SocialLinks/SocialShareSheetCTA'
import { UserRAFLink } from 'UserRAFLink'
import Immutable from 'immutable'
import { Helmet } from 'react-helmet'

import Loading from 'routes/Menu/Loading'

import { BoostedRafSharing } from './BoostedRafSharing/BoostedRafSharing'
import { HowItWorks } from './HowItWorks'
import { RAFOffer } from './RAFOffer'
import { RAFTitle } from './RAFTitle'
import { RafSharing } from './RafSharing/RafSharing'

import css from './Referral.css'

export type IRAFOffer = Immutable.Map<
  string,
  | string
  | string[]
  | { value: number; unit: string; formatted: string }
  | Immutable.Map<string, string>
>
interface Props {
  device: string
  displayLink: string
  expiry?: string | null
  isLoading: boolean
  offerDetails: Immutable.List<string>
  offerTitle: string
  rafOffer: IRAFOffer
  referralCode: string
  trackingReferFriend: () => void
  trackingReferFriendSocialSharing: () => void
  trackUserFreeFoodLinkShare: () => void
  userFirstName: string
}

export const ReferralPresentation: React.FC<Props> = ({
  device,
  displayLink,
  expiry,
  isLoading,
  offerDetails,
  offerTitle,
  rafOffer,
  referralCode,
  trackingReferFriend,
  trackingReferFriendSocialSharing,
  trackUserFreeFoodLinkShare,
  userFirstName,
}: Props) => {
  const [{ enabled: isBoostedRafSharingEnabled }] = useDecision('tomatoes_ofx_raf_boost_ui_refresh')
  const isSteppedDiscount = !!rafOffer.get('secondBoxDiscountFormatted') // New tiered RAF offer  (e.g. 60% off first box, 40% off second box, 30% off third box)

  if (isLoading) {
    return (
      <div className={css.loadingContainer}>
        <Loading loading />
      </div>
    )
  }

  // boost view takes priority over non-boosted views - show this view when boost ui is enabled and expiry date
  if (expiry && !!isBoostedRafSharingEnabled) {
    return (
      <div>
        <Helmet title="Gousto Referrals | Invite Your Friends To Try Gousto" />
        <div id="referral-code-box" className={css.containerBackgroundRafBoostRefreshUI}>
          <BoostedRafSharing
            rafOffer={rafOffer}
            expiry={new Date(expiry)}
            userRAFLink={displayLink}
            userFirstName={userFirstName}
            device={device}
            trackingReferFriendSocialSharing={trackingReferFriendSocialSharing}
            trackUserFreeFoodLinkShare={trackUserFreeFoodLinkShare}
            trackingReferFriend={trackingReferFriend}
            referralCode={referralCode}
          />
        </div>
      </div>
    )
  }

  // then check if the offer is a stepped discount
  if (isSteppedDiscount) {
    return (
      <div>
        <Helmet title="Gousto Referrals | Invite Your Friends To Try Gousto" />
        <div id="referral-code-box">
          <RafSharing
            device={device}
            userRAFLink={displayLink}
            userFirstName={userFirstName}
            offer={rafOffer}
            offerTitle={offerTitle}
            referralCode={referralCode}
            trackingReferFriendSocialSharing={trackingReferFriendSocialSharing}
            trackUserFreeFoodLinkShare={trackUserFreeFoodLinkShare}
            trackingReferFriend={trackingReferFriend}
          />
        </div>
      </div>
    )
  }

  // else show the non-stepped discount view
  return (
    <div className={expiry ? css.containerBackgroundDouble : css.containerBackground}>
      <Helmet title="Gousto Referrals | Invite Your Friends To Try Gousto" />
      <div className={css.rafPageTitle}>
        <RAFTitle title={offerTitle} />
      </div>
      <div className={css.rafOfferSection}>
        <div className={css.rafOfferBanner}>
          <div className={expiry ? css.iconReferDouble : css.iconRefer} />
          <RAFOffer offer={rafOffer} />
        </div>
        <div className={expiry ? css.rafCounterPresent : css.rafRow}>
          <UserRAFLink
            classContainer={css.rafLink}
            classLinkContainer={css.linkContainer}
            referralCode={referralCode}
            trackingReferFriend={trackingReferFriend}
            trackUserFreeFoodLinkShare={trackUserFreeFoodLinkShare}
          >
            <div id="referral-code-box" aria-label="referral code">
              <textarea className={`${css.displayedLink}`} readOnly>
                {displayLink}
              </textarea>
            </div>
          </UserRAFLink>
          <SocialIconShareButtons
            referralCode={referralCode}
            userFirstName={userFirstName}
            device={device}
            elementType="page"
            trackingReferFriendSocialSharing={trackingReferFriendSocialSharing}
            trackUserFreeFoodLinkShare={trackUserFreeFoodLinkShare}
          />
          <div className={css.mobileShow}>
            <SocialShareSheetCTA
              referralCode={referralCode}
              trackingReferFriend={trackingReferFriend}
              isFixed
            />
          </div>
        </div>
      </div>
      <HowItWorks details={offerDetails} />
    </div>
  )
}
